import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const SWPCalculatorScreen = () => {
  const { colors } = useTheme();
  const [initialCorpus, setInitialCorpus] = useState('2000000');
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState('15000');
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [withdrawalFrequency, setWithdrawalFrequency] = useState('monthly');
  const [inflationRate, setInflationRate] = useState(6);
  const [taxRate, setTaxRate] = useState(10);
  const [investmentType, setInvestmentType] = useState('equity');
  const [withdrawalPeriod, setWithdrawalPeriod] = useState(20);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showSustainability, setShowSustainability] = useState(false);
  const [showYearlyBreakdown, setShowYearlyBreakdown] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);

  const frequencyOptions = [
    { label: 'Monthly', value: 'monthly', multiplier: 12 },
    { label: 'Quarterly', value: 'quarterly', multiplier: 4 },
    { label: 'Half-Yearly', value: 'half-yearly', multiplier: 2 },
    { label: 'Yearly', value: 'yearly', multiplier: 1 },
  ];

  const investmentTypes = [
    { label: 'Equity Mutual Funds', value: 'equity', expectedReturn: 12, risk: 'High' },
    { label: 'Hybrid Funds', value: 'hybrid', expectedReturn: 10, risk: 'Medium' },
    { label: 'Debt Funds', value: 'debt', expectedReturn: 8, risk: 'Low' },
    { label: 'Fixed Deposits', value: 'fd', expectedReturn: 6.5, risk: 'Very Low' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateSWP = () => {
    const corpus = parseFloat(initialCorpus) || 0;
    const withdrawal = parseFloat(monthlyWithdrawal) || 0;
    const returnRate = parseFloat(expectedReturn) || 0;
    const inflation = parseFloat(inflationRate) || 0;
    const tax = parseFloat(taxRate) || 0;
    const period = parseFloat(withdrawalPeriod) || 0;

    const frequency = frequencyOptions.find(f => f.value === withdrawalFrequency);
    const withdrawalsPerYear = frequency ? frequency.multiplier : 12;
    const annualWithdrawal = withdrawal * withdrawalsPerYear;

    // Calculate monthly return rate
    const monthlyReturn = returnRate / (12 * 100);
    const monthlyInflation = inflation / (12 * 100);
    const realReturn = ((1 + returnRate/100) / (1 + inflation/100) - 1) * 100;

    let remainingCorpus = corpus;
    let totalWithdrawn = 0;
    let yearlyBreakdown = [];
    let corpusDepletionYear = null;

    // Calculate year-wise breakdown
    for (let year = 1; year <= period; year++) {
      let yearStartCorpus = remainingCorpus;
      let yearWithdrawal = 0;
      let yearEndCorpus = remainingCorpus;

      // Calculate monthly for this year
      for (let month = 1; month <= 12; month++) {
        if (remainingCorpus <= 0) break;

        // Apply monthly return
        remainingCorpus = remainingCorpus * (1 + monthlyReturn);

        // Withdraw if it's the withdrawal month
        if (withdrawalFrequency === 'monthly' || 
            (withdrawalFrequency === 'quarterly' && month % 3 === 1) ||
            (withdrawalFrequency === 'half-yearly' && (month === 1 || month === 7)) ||
            (withdrawalFrequency === 'yearly' && month === 1)) {
          
          let currentWithdrawal = withdrawal;
          
          // Apply inflation adjustment
          currentWithdrawal = withdrawal * Math.pow(1 + inflation/100, year - 1);
          
          // Apply tax on withdrawal (if applicable)
          let taxAmount = currentWithdrawal * (tax / 100);
          let netWithdrawal = currentWithdrawal + taxAmount;

          if (remainingCorpus >= netWithdrawal) {
            remainingCorpus -= netWithdrawal;
            yearWithdrawal += currentWithdrawal;
            totalWithdrawn += currentWithdrawal;
          } else {
            // Partial withdrawal possible
            let possibleWithdrawal = remainingCorpus / (1 + tax/100);
            yearWithdrawal += possibleWithdrawal;
            totalWithdrawn += possibleWithdrawal;
            remainingCorpus = 0;
            if (!corpusDepletionYear) {
              corpusDepletionYear = year + (month - 1) / 12;
            }
            break;
          }
        }
      }

      yearEndCorpus = remainingCorpus;

      yearlyBreakdown.push({
        year,
        startCorpus: yearStartCorpus,
        withdrawal: yearWithdrawal,
        endCorpus: yearEndCorpus,
        inflationAdjustedWithdrawal: withdrawal * Math.pow(1 + inflation/100, year - 1),
      });

      if (remainingCorpus <= 0) break;
    }

    // Calculate sustainability metrics
    const totalReturns = corpus * Math.pow(1 + returnRate/100, period) - corpus;
    const sustainabilityRatio = (remainingCorpus / corpus) * 100;
    const averageAnnualWithdrawal = totalWithdrawn / period;

    // Calculate optimal withdrawal rate
    const safeWithdrawalRate = (realReturn > 0 ? realReturn : 4) / 100;
    const optimalMonthlyWithdrawal = (corpus * safeWithdrawalRate) / 12;

    return {
      initialCorpus: corpus,
      totalWithdrawn,
      remainingCorpus: Math.max(0, remainingCorpus),
      corpusDepletionYear,
      yearlyBreakdown,
      sustainabilityRatio,
      averageAnnualWithdrawal,
      realReturn,
      optimalMonthlyWithdrawal,
      totalTaxPaid: totalWithdrawn * (tax / 100),
      inflationImpact: (withdrawal * Math.pow(1 + inflation/100, period)) - withdrawal,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateSWP();
    setResults(calculatedResults);
  }, [initialCorpus, monthlyWithdrawal, expectedReturn, withdrawalFrequency, inflationRate, taxRate, withdrawalPeriod]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>SWP Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your Systematic Withdrawal Plan and corpus sustainability timeline
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>SWP Details</Text>
      
      {/* Investment Type Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Investment Type</Text>
        <View style={styles.segmentedControl}>
          {investmentTypes.map((type) => (
            <TouchableOpacity
              key={type.value}
              style={[
                styles.segmentButton,
                investmentType === type.value && styles.segmentButtonActive,
              ]}
              onPress={() => {
                setInvestmentType(type.value);
                setExpectedReturn(type.expectedReturn);
              }}
            >
              <Text style={[
                styles.segmentButtonText,
                investmentType === type.value && styles.segmentButtonTextActive,
              ]}>
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Initial Corpus */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Initial Corpus</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={initialCorpus}
            onChangeText={setInitialCorpus}
            placeholder="2000000"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Withdrawal Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Withdrawal Amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={monthlyWithdrawal}
            onChangeText={setMonthlyWithdrawal}
            placeholder="15000"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Withdrawal Frequency */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Withdrawal Frequency</Text>
        <View style={styles.segmentedControl}>
          {frequencyOptions.map((freq) => (
            <TouchableOpacity
              key={freq.value}
              style={[
                styles.segmentButton,
                withdrawalFrequency === freq.value && styles.segmentButtonActive,
              ]}
              onPress={() => setWithdrawalFrequency(freq.value)}
            >
              <Text style={[
                styles.segmentButtonText,
                withdrawalFrequency === freq.value && styles.segmentButtonTextActive,
              ]}>
                {freq.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Expected Return */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Expected Annual Return: {expectedReturn}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={4}
          maximumValue={18}
          value={expectedReturn}
          onValueChange={setExpectedReturn}
          step={0.5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>4%</Text>
          <Text style={styles.sliderLabel}>18%</Text>
        </View>
      </View>

      {/* Withdrawal Period */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Withdrawal Period: {withdrawalPeriod} years</Text>
        <Slider
          style={styles.slider}
          minimumValue={5}
          maximumValue={40}
          value={withdrawalPeriod}
          onValueChange={setWithdrawalPeriod}
          step={1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>5 years</Text>
          <Text style={styles.sliderLabel}>40 years</Text>
        </View>
      </View>

      {/* Inflation Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Inflation Rate: {inflationRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={3}
          maximumValue={10}
          value={inflationRate}
          onValueChange={setInflationRate}
          step={0.5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>3%</Text>
          <Text style={styles.sliderLabel}>10%</Text>
        </View>
      </View>

      {/* Tax Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tax Rate on Withdrawals: {taxRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={0}
          maximumValue={30}
          value={taxRate}
          onValueChange={setTaxRate}
          step={1}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0%</Text>
          <Text style={styles.sliderLabel}>30%</Text>
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { initialCorpus, totalWithdrawn, remainingCorpus } = results;
    const totalValue = initialCorpus;
    const withdrawnPercentage = (totalWithdrawn / totalValue) * 100;
    const remainingPercentage = (remainingCorpus / totalValue) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const withdrawnStrokeDasharray = `${(withdrawnPercentage / 100) * circumference} ${circumference}`;
    const remainingStrokeDasharray = `${(remainingPercentage / 100) * circumference} ${circumference}`;
    const remainingStrokeDashoffset = -((withdrawnPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>Corpus Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Withdrawn amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.error}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={withdrawnStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Remaining corpus */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={remainingStrokeDasharray}
              strokeDashoffset={remainingStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Total Corpus
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(initialCorpus)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.error }]} />
            <Text style={styles.legendText}>Total Withdrawn ({withdrawnPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Remaining Corpus ({remainingPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      totalWithdrawn, 
      remainingCorpus, 
      corpusDepletionYear, 
      sustainabilityRatio,
      optimalMonthlyWithdrawal,
      totalTaxPaid,
      inflationImpact
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Calculation Results</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Withdrawn</Text>
          <Text style={styles.resultValue}>{formatCurrency(totalWithdrawn)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Remaining Corpus</Text>
          <Text style={[styles.resultValue, { color: remainingCorpus > 0 ? colors.success : colors.error }]}>
            {formatCurrency(remainingCorpus)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Sustainability Ratio</Text>
          <Text style={[styles.resultValue, { color: sustainabilityRatio > 50 ? colors.success : colors.warning }]}>
            {sustainabilityRatio.toFixed(1)}%
          </Text>
        </View>

        {corpusDepletionYear && (
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>Corpus Depletion</Text>
            <Text style={[styles.resultValue, { color: colors.error }]}>
              Year {corpusDepletionYear.toFixed(1)}
            </Text>
          </View>
        )}
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total Tax Paid</Text>
          <Text style={styles.resultValue}>{formatCurrency(totalTaxPaid)}</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Inflation Impact</Text>
          <Text style={styles.resultValue}>{formatCurrency(inflationImpact)}</Text>
        </View>

        <View style={styles.recommendationBox}>
          <Text style={styles.recommendationTitle}>💡 Optimal Withdrawal</Text>
          <Text style={styles.recommendationText}>
            For sustainable withdrawals, consider {formatCurrency(optimalMonthlyWithdrawal)} per month 
            based on a safe withdrawal rate.
          </Text>
        </View>
      </View>
    );
  };

  const renderSustainabilityAnalysis = () => {
    if (!results) return null;

    const { sustainabilityRatio, realReturn, corpusDepletionYear } = results;
    
    let sustainabilityStatus = 'Excellent';
    let sustainabilityColor = colors.success;
    let recommendation = 'Your withdrawal plan is highly sustainable with good corpus preservation.';

    if (sustainabilityRatio < 20) {
      sustainabilityStatus = 'Poor';
      sustainabilityColor = colors.error;
      recommendation = 'Consider reducing withdrawal amount or increasing expected returns.';
    } else if (sustainabilityRatio < 50) {
      sustainabilityStatus = 'Moderate';
      sustainabilityColor = colors.warning;
      recommendation = 'Your plan is moderately sustainable. Monitor and adjust as needed.';
    }

    return (
      <View style={styles.sustainabilityCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowSustainability(!showSustainability)}
        >
          <Text style={styles.cardTitle}>Sustainability Analysis</Text>
          <Ionicons
            name={showSustainability ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showSustainability && (
          <View style={styles.cardContent}>
            <View style={styles.sustainabilityHeader}>
              <Text style={styles.sustainabilityTitle}>Corpus Sustainability</Text>
              <View style={[styles.sustainabilityBadge, { backgroundColor: sustainabilityColor }]}>
                <Text style={styles.sustainabilityBadgeText}>{sustainabilityStatus}</Text>
              </View>
            </View>
            
            <View style={styles.sustainabilityMetrics}>
              <View style={styles.sustainabilityMetric}>
                <Text style={styles.metricLabel}>Real Return (Inflation Adjusted)</Text>
                <Text style={styles.metricValue}>{realReturn.toFixed(2)}%</Text>
              </View>
              
              <View style={styles.sustainabilityMetric}>
                <Text style={styles.metricLabel}>Corpus Preservation</Text>
                <Text style={styles.metricValue}>{sustainabilityRatio.toFixed(1)}%</Text>
              </View>
              
              {corpusDepletionYear && (
                <View style={styles.sustainabilityMetric}>
                  <Text style={styles.metricLabel}>Estimated Depletion</Text>
                  <Text style={styles.metricValue}>Year {corpusDepletionYear.toFixed(1)}</Text>
                </View>
              )}
            </View>
            
            <View style={styles.recommendationBox}>
              <Text style={styles.recommendationTitle}>Recommendation</Text>
              <Text style={styles.recommendationText}>{recommendation}</Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderYearlyBreakdown = () => {
    if (!results || !results.yearlyBreakdown) return null;

    const { yearlyBreakdown } = results;

    return (
      <View style={styles.breakdownCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowYearlyBreakdown(!showYearlyBreakdown)}
        >
          <Text style={styles.cardTitle}>Year-wise Breakdown</Text>
          <Ionicons
            name={showYearlyBreakdown ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showYearlyBreakdown && (
          <View style={styles.cardContent}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, { flex: 1 }]}>Year</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>Start Corpus</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>Withdrawal</Text>
              <Text style={[styles.tableHeaderText, { flex: 2 }]}>End Corpus</Text>
            </View>
            
            <ScrollView style={styles.tableContainer} nestedScrollEnabled>
              {yearlyBreakdown.slice(0, displayedRows).map((row, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 1 }]}>{row.year}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.startCorpus)}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.withdrawal)}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{formatCurrency(row.endCorpus)}</Text>
                </View>
              ))}
            </ScrollView>
            
            {yearlyBreakdown.length > displayedRows && (
              <TouchableOpacity
                style={styles.showMoreButton}
                onPress={() => setDisplayedRows(displayedRows + 5)}
              >
                <Text style={styles.showMoreText}>Show More Years</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About SWP (Systematic Withdrawal Plan)</Text>
      <Text style={styles.infoSubtitle}>Understanding Systematic Withdrawals</Text>
      
      <Text style={styles.infoText}>
        A Systematic Withdrawal Plan (SWP) allows you to withdraw a fixed amount from your mutual fund 
        investments at regular intervals. It's an ideal strategy for generating regular income during 
        retirement while keeping your money invested for potential growth.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="bulb" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Pro Tip:</Text> Consider the 4% rule - withdrawing 4% 
          of your corpus annually is generally considered safe for long-term sustainability.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="trending-up" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Growth Potential:</Text> Unlike fixed deposits, SWP 
          allows your remaining corpus to continue growing, potentially beating inflation over time.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Tax Efficiency:</Text> SWP can be more tax-efficient 
          than traditional income sources, especially for equity mutual funds held for over a year.
        </Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>💰 Complete Guide to SWP Calculator & Systematic Withdrawal Planning</Text>
      <Text style={styles.seoSubtitle}>Master the Art of Regular Income Generation from Your Investments</Text>
      
      <Text style={styles.seoText}>
        Our advanced SWP (Systematic Withdrawal Plan) calculator helps you plan sustainable income from your mutual fund investments. 
        Whether you're planning for retirement, need regular income, or want to optimize your withdrawal strategy, this tool provides 
        comprehensive analysis including corpus sustainability, tax implications, and inflation impact.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 What is SWP (Systematic Withdrawal Plan)?</Text>
        <Text style={styles.seoText}>
          A Systematic Withdrawal Plan (SWP) is an investment strategy that allows you to withdraw a predetermined amount from your 
          mutual fund investments at regular intervals. Unlike traditional fixed deposits, SWP keeps your remaining corpus invested, 
          allowing it to potentially grow while providing regular income.
        </Text>
        
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>✅ SWP Benefits</Text>
            <Text style={styles.comparisonText}>• Regular income stream</Text>
            <Text style={styles.comparisonText}>• Corpus continues to grow</Text>
            <Text style={styles.comparisonText}>• Tax-efficient withdrawals</Text>
            <Text style={styles.comparisonText}>• Inflation protection potential</Text>
          </View>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>⚠️ Considerations</Text>
            <Text style={styles.comparisonText}>• Market volatility risk</Text>
            <Text style={styles.comparisonText}>• Corpus depletion possibility</Text>
            <Text style={styles.comparisonText}>• Requires careful planning</Text>
            <Text style={styles.comparisonText}>• Exit load implications</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🔧 Key Features of Our SWP Calculator</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>📊 <Text style={{fontWeight: 'bold'}}>Comprehensive Analysis:</Text> Calculate total withdrawals, remaining corpus, and sustainability ratios</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>📈 <Text style={{fontWeight: 'bold'}}>Multiple Investment Types:</Text> Support for equity, hybrid, debt funds, and fixed deposits</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>⏰ <Text style={{fontWeight: 'bold'}}>Flexible Frequency:</Text> Monthly, quarterly, half-yearly, or yearly withdrawal options</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>💹 <Text style={{fontWeight: 'bold'}}>Inflation Impact:</Text> Real-time calculation of inflation effect on purchasing power</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>🎯 <Text style={{fontWeight: 'bold'}}>Optimal Withdrawal:</Text> Recommendations based on safe withdrawal rates</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureText}>📋 <Text style={{fontWeight: 'bold'}}>Year-wise Breakdown:</Text> Detailed analysis of corpus depletion timeline</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 SWP vs Other Income Options</Text>
        <View style={styles.taxRateTable}>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateHeader}>Income Source</Text>
            <Text style={styles.taxRateHeader}>Growth Potential</Text>
            <Text style={styles.taxRateHeader}>Tax Efficiency</Text>
            <Text style={styles.taxRateHeader}>Liquidity</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>SWP (Equity Funds)</Text>
            <Text style={styles.taxRateCell}>High</Text>
            <Text style={styles.taxRateCell}>Excellent</Text>
            <Text style={styles.taxRateCell}>High</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Fixed Deposits</Text>
            <Text style={styles.taxRateCell}>Low</Text>
            <Text style={styles.taxRateCell}>Poor</Text>
            <Text style={styles.taxRateCell}>Medium</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Dividend Stocks</Text>
            <Text style={styles.taxRateCell}>Medium</Text>
            <Text style={styles.taxRateCell}>Good</Text>
            <Text style={styles.taxRateCell}>High</Text>
          </View>
          <View style={styles.taxRateRow}>
            <Text style={styles.taxRateCell}>Rental Income</Text>
            <Text style={styles.taxRateCell}>Medium</Text>
            <Text style={styles.taxRateCell}>Poor</Text>
            <Text style={styles.taxRateCell}>Low</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📝 How to Use the SWP Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>1</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Enter Initial Corpus:</Text> Input your total investment amount or current mutual fund value</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>2</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Set Withdrawal Amount:</Text> Specify how much you want to withdraw regularly</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>3</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Choose Investment Type:</Text> Select from equity, hybrid, debt funds, or fixed deposits</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>4</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Select Frequency:</Text> Choose monthly, quarterly, half-yearly, or yearly withdrawals</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>5</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Set Parameters:</Text> Input expected returns, inflation rate, and tax rate</Text>
          </View>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}><Text style={styles.stepNumberText}>6</Text></View>
            <Text style={styles.stepText}><Text style={{fontWeight: 'bold'}}>Analyze Results:</Text> Review sustainability ratios, corpus depletion timeline, and recommendations</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 SWP Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>🎯 4% Rule Strategy</Text>
              <Text style={styles.strategyText}>
                Follow the widely accepted 4% annual withdrawal rule for long-term sustainability. This means withdrawing 4% of your initial corpus annually, adjusted for inflation.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>📊 Dynamic Withdrawal</Text>
              <Text style={styles.strategyText}>
                Adjust your withdrawal amount based on market performance. Reduce withdrawals during market downturns and increase during bull markets.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>🔄 Bucket Strategy</Text>
              <Text style={styles.strategyText}>
                Divide your corpus into different buckets - conservative for immediate needs, moderate for medium-term, and aggressive for long-term growth.
              </Text>
            </View>
          </View>
          <View style={styles.strategyItem}>
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>💰 Tax Optimization</Text>
              <Text style={styles.strategyText}>
                Plan withdrawals to optimize tax efficiency. For equity funds, utilize the ₹1 lakh annual LTCG exemption and consider tax-loss harvesting.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⚖️ Legal & Regulatory Considerations</Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Tax Implications:</Text> SWP withdrawals are treated as redemptions for tax purposes. 
          For equity mutual funds held for more than 1 year, LTCG tax applies at 10% on gains exceeding ₹1 lakh annually. 
          For debt funds, gains are taxed as per your income tax slab if held for less than 3 years.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Exit Load:</Text> Most mutual funds charge exit load if you redeem within a specified period 
          (usually 1 year for equity funds). Plan your SWP start date accordingly to avoid exit load charges.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Minimum Balance:</Text> Ensure your remaining investment doesn't fall below the fund's 
          minimum balance requirement, which could lead to automatic redemption of the entire amount.
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🚫 Common SWP Mistakes to Avoid</Text>
        <Text style={styles.seoText}>
          • <Text style={{fontWeight: 'bold'}}>Over-withdrawal:</Text> Setting withdrawal rates above 6-7% annually can lead to rapid corpus depletion
        </Text>
        <Text style={styles.seoText}>
          • <Text style={{fontWeight: 'bold'}}>Ignoring Inflation:</Text> Not accounting for inflation can erode your purchasing power over time
        </Text>
        <Text style={styles.seoText}>
          • <Text style={{fontWeight: 'bold'}}>Wrong Fund Choice:</Text> Using volatile funds for SWP can lead to sequence of returns risk
        </Text>
        <Text style={styles.seoText}>
          • <Text style={{fontWeight: 'bold'}}>Fixed Mindset:</Text> Not adjusting withdrawal amounts based on market conditions and life changes
        </Text>
        <Text style={styles.seoText}>
          • <Text style={{fontWeight: 'bold'}}>Tax Inefficiency:</Text> Not optimizing withdrawals for tax benefits and exemptions
        </Text>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📈 SWP Success Examples</Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Conservative Retiree:</Text> ₹50 lakh corpus, ₹15,000 monthly withdrawal (3.6% annually) 
          from balanced funds can potentially last 25+ years with moderate growth.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Aggressive Investor:</Text> ₹1 crore corpus, ₹40,000 monthly withdrawal (4.8% annually) 
          from equity funds with potential for corpus growth despite withdrawals.
        </Text>
        <Text style={styles.seoText}>
          <Text style={{fontWeight: 'bold'}}>Flexible Approach:</Text> Starting with 3% withdrawal rate and increasing by inflation rate 
          annually while monitoring corpus sustainability.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is the ideal withdrawal rate for SWP?",
        answer: "The widely accepted safe withdrawal rate is 4% annually. However, this can vary based on your risk tolerance, market conditions, and life expectancy. Conservative investors might prefer 3-3.5%, while aggressive investors might go up to 5-6%."
      },
      {
        question: "How does inflation affect my SWP?",
        answer: "Inflation reduces the purchasing power of your withdrawals over time. To maintain your lifestyle, you should either increase your withdrawal amount annually by the inflation rate or invest in assets that can potentially beat inflation."
      },
      {
        question: "Which funds are best for SWP?",
        answer: "Balanced or hybrid funds are often preferred for SWP as they provide stability with growth potential. Large-cap equity funds and conservative hybrid funds are popular choices for regular income generation."
      },
      {
        question: "Can I change my SWP amount?",
        answer: "Yes, most mutual fund companies allow you to modify your SWP amount, frequency, or even pause it temporarily. However, check with your fund house for specific terms and conditions."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
            
            <View style={styles.faqItem}>
              <Text style={styles.faqQuestion}>What are the tax implications of SWP?</Text>
              <Text style={styles.faqAnswer}>
                Tax treatment depends on the type of fund and holding period. For equity funds, 
                gains up to ₹1 lakh per year are tax-free if held for more than a year. For debt funds, 
                gains are taxed based on your income tax slab if held for less than 3 years.
              </Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    segmentedControl: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
    },
    segmentButton: {
      flex: 1,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      alignItems: 'center',
    },
    segmentButtonActive: {
      backgroundColor: colors.primary,
    },
    segmentButtonText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    segmentButtonTextActive: {
      color: colors.background,
    },
    slider: {
      height: 40,
      marginVertical: 10,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    sustainabilityCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    sustainabilityHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    sustainabilityTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    sustainabilityBadge: {
      paddingHorizontal: 12,
      paddingVertical: 4,
      borderRadius: 12,
    },
    sustainabilityBadgeText: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.background,
    },
    sustainabilityMetrics: {
      gap: 12,
    },
    sustainabilityMetric: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    metricLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    metricValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    recommendationBox: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 12,
    },
    recommendationTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    recommendationText: {
      fontSize: 13,
      color: colors.textSecondary,
      lineHeight: 18,
    },
    breakdownCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      borderRadius: 8,
      padding: 12,
      marginBottom: 8,
    },
    tableHeaderText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.background,
      textAlign: 'center',
    },
    tableContainer: {
      maxHeight: 300,
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    tableCell: {
      fontSize: 13,
      color: colors.text,
      textAlign: 'center',
    },
    showMoreButton: {
      backgroundColor: colors.primary,
      borderRadius: 8,
      padding: 12,
      alignItems: 'center',
      marginTop: 12,
    },
    showMoreText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: '600',
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
    seoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 18,
      color: colors.primary,
      marginBottom: 20,
      textAlign: 'center',
      fontWeight: '600',
    },
    seoText: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 24,
    },
    seoSectionTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      flexDirection: 'row',
      gap: 16,
      marginTop: 12,
    },
    comparisonItem: {
      flex: 1,
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featuresList: {
      gap: 12,
    },
    featureItem: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    taxRateTable: {
      backgroundColor: colors.background,
      borderRadius: 8,
      overflow: 'hidden',
    },
    taxRateRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    taxRateHeader: {
      flex: 1,
      padding: 12,
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      backgroundColor: colors.primary + '20',
    },
    taxRateCell: {
      flex: 1,
      padding: 12,
      fontSize: 13,
      color: colors.textSecondary,
    },
    stepsList: {
      gap: 16,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    stepNumber: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    stepNumberText: {
      color: colors.background,
      fontSize: 14,
      fontWeight: 'bold',
    },
    stepText: {
      flex: 1,
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    strategiesList: {
      gap: 16,
    },
    strategyItem: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
    },
    strategyContent: {
      gap: 8,
    },
    strategyTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
    },
    strategyText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderSustainabilityAnalysis()}
      {renderYearlyBreakdown()}
      {renderInformation()}
      {renderSEOContent()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default SWPCalculatorScreen;